import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { monthOf, daysInMonth, fmtTime } from "@/lib/date";
import { Download, FileText, X, MapPin, Pencil, Trash2, Save } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [month, setMonth] = useState(monthOf());
  const [uid, setUid] = useState("");
  const [att, setAtt] = useState([]);
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [detailUser, setDetailUser] = useState(null);

  const load = async () => {
    if (!user) return;
    try {
      const [a, u, l, h] = await Promise.all([
        api.get(`/attendance?month=${month}`),
        api.get("/users"),
        api.get("/leaves"),
        api.get("/holidays"),
      ]);
      setAtt(a.data); setUsers(u.data); setLeaves(l.data); setHolidays(h.data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user, month]);

  const isStaff = user?.role === "admin" || user?.role === "hr_manager";

  const reports = useMemo(() => {
    const list = isStaff ? (uid ? users.filter((u) => u.user_id === uid) : users) : users.filter((u) => u.user_id === user?.user_id);
    const [y, m] = month.split("-").map(Number);
    const total = daysInMonth(month);
    const today = new Date();
    return list.map((u) => {
      let workDays = 0, holCount = 0;
      for (let d = 1; d <= total; d++) {
        const dt = new Date(y, m - 1, d);
        if (dt > today) break;
        const iso = `${month}-${String(d).padStart(2, "0")}`;
        const isHol = holidays.some((h) => h.date === iso);
        if (isHol) { holCount++; continue; }
        if (dt.getDay() === 0 && (settings?.sundays_optional ?? true)) continue;
        workDays++;
      }
      const myAtt = att.filter((a) => a.user_id === u.user_id);
      const present = myAtt.filter((a) => a.check_in && a.check_out).length;
      const half = myAtt.filter((a) => a.check_in && !a.check_out).length;
      const lv = leaves.filter((l) => l.user_id === u.user_id && l.status === "approved" && l.date.startsWith(month)).length;
      const absent = Math.max(0, workDays - present - half - lv);
      const pct = workDays ? Math.round(((present + 0.5 * half) / workDays) * 100) : 0;
      return { user: u, present, absent, half, leave: lv, holiday: holCount, workDays, pct };
    });
  }, [users, att, leaves, holidays, month, user, uid, settings]);

  const exportCSV = () => {
    const rows = [["Name","Department","Work Days","Present","Absent","Half Day","Leave","Holiday","Attendance %"]];
    reports.forEach((r) => rows.push([r.user.name, r.user.department || "", r.workDays, r.present, r.absent, r.half, r.leave, r.holiday, `${r.pct}%`]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `attendance-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Shared: build per-day rows for a single employee in the selected month
  const buildRowsFor = useMemo(() => (u) => {
    if (!u) return [];
    const startMin = (() => {
      const s = settings?.work_start_time || "10:45";
      const [h, m] = s.split(":").map(Number); return h * 60 + m;
    })();
    const endMin = (() => {
      const s = settings?.work_end_time || "20:45";
      const [h, m] = s.split(":").map(Number); return h * 60 + m;
    })();
    const grace = Number(settings?.grace_period || 0);
    const [y, m] = month.split("-").map(Number);
    const total = daysInMonth(month);
    const today = new Date();
    const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const minutesOfIso = (iso) => {
      if (!iso) return null;
      try { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes(); } catch { return null; }
    };
    const hoursBetween = (a, b) => {
      if (!a || !b) return "";
      try { const diffMs = new Date(b) - new Date(a); if (diffMs <= 0) return ""; return (diffMs / 3600000).toFixed(2); } catch { return ""; }
    };
    const rows = [];
    for (let d = 1; d <= total; d++) {
      const dt = new Date(y, m - 1, d);
      if (dt > today) break;
      const iso = `${month}-${String(d).padStart(2, "0")}`;
      const dow = dt.getDay();
      const isHol = holidays.find((h) => h.date === iso);
      const onLeave = leaves.find((l) => l.user_id === u.user_id && l.date === iso && l.status === "approved");
      const a = att.find((x) => x.user_id === u.user_id && x.date === iso);
      let status;
      if (isHol) status = `Holiday`;
      else if (onLeave) status = `Leave (${onLeave.type || "casual"})`;
      else if (a?.check_in && a?.check_out) status = "Present";
      else if (a?.check_in && !a?.check_out) status = "Half Day";
      else if (dow === 0 && (settings?.sundays_optional ?? true)) status = "Sunday (Off)";
      else status = "Absent";
      const inMin = minutesOfIso(a?.check_in);
      const outMin = minutesOfIso(a?.check_out);
      const late = inMin != null && inMin > startMin + grace ? inMin - startMin : 0;
      const early = outMin != null && outMin < endMin - grace ? endMin - outMin : 0;
      rows.push({
        date: iso, day: dayNames[dow],
        att_id: a?.id || null,
        check_in: fmtTime(a?.check_in), check_out: fmtTime(a?.check_out),
        hours: hoursBetween(a?.check_in, a?.check_out),
        status, late, early,
        regularized: !!a?.regularized,
        ci_lat: a?.check_in_lat, ci_lng: a?.check_in_lng,
        co_lat: a?.check_out_lat, co_lng: a?.check_out_lng,
        ci_address: a?.check_in_address || "",
        co_address: a?.check_out_address || "",
      });
    }
    return rows;
  }, [month, att, leaves, holidays, settings]);

  const exportDetailedCSV = () => {
    const list = isStaff
      ? (uid ? users.filter((u) => u.user_id === uid) : users)
      : users.filter((u) => u.user_id === user?.user_id);
    const rows = [[
      "Date","Day","Employee","Department","Check-In","Check-Out","Hours","Status","Late","Early","Regularized","Check-In Address","Check-Out Address","Check-In Lat","Check-In Lng","Check-Out Lat","Check-Out Lng",
    ]];
    list.forEach((u) => {
      buildRowsFor(u).forEach((r) => {
        rows.push([
          r.date, r.day, u.name, u.department || "",
          r.check_in, r.check_out, r.hours, r.status,
          r.late ? `${r.late}m` : "",
          r.early ? `${r.early}m` : "",
          r.regularized ? "Yes" : "",
          r.ci_address || "",
          r.co_address || "",
          r.ci_lat ?? "", r.ci_lng ?? "", r.co_lat ?? "", r.co_lng ?? "",
        ]);
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `attendance-detailed-${month}${uid ? `-${list[0]?.name || "user"}` : ""}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const loadAttendance = async () => {
    try { const { data } = await api.get(`/attendance?month=${month}`); setAtt(data); } catch {}
  };

  const editAttendance = async (att_id, body) => {
    await api.patch(`/attendance/${att_id}`, body);
    await loadAttendance();
  };

  const createAttendance = async (user_id, date, body) => {
    await api.post(`/attendance/manual`, { user_id, date, ...body });
    await loadAttendance();
  };

  const deleteAttendance = async (att_id) => {
    if (!window.confirm("Delete this attendance row? This cannot be undone.")) return;
    await api.delete(`/attendance/${att_id}`);
    await loadAttendance();
  };

  const canEditAttendance = user?.role === "admin" || (user?.role === "hr_manager" && !!settings?.hr_can_edit_attendance);

  return (
    <div className="px-4 pt-3 pb-4 space-y-3" data-testid="reports-page">
      <div className="flex items-center justify-between gap-2">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="reports-month"
          className="flex-1 h-11 px-3 rounded-xl font-mono text-sm" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#E8ECFF" }} />
        <button onClick={exportCSV} data-testid="export-csv-button" title="Summary CSV (totals per employee)" className="h-11 px-3 rounded-xl flex items-center gap-1.5 font-bold text-sm" style={{ background: "#22D3A5", color: "#0A0D14" }}>
          <Download size={14} /> Summary
        </button>
        <button onClick={exportDetailedCSV} data-testid="export-detailed-csv-button" title="Detailed CSV (per-day with check-in/check-out times)" className="h-11 px-3 rounded-xl flex items-center gap-1.5 font-bold text-sm" style={{ background: "#4F8EF7", color: "#fff" }}>
          <FileText size={14} /> Detailed
        </button>
      </div>

      {isStaff && (
        <select value={uid} onChange={(e) => setUid(e.target.value)} data-testid="reports-user-filter"
          className="w-full h-11 px-3 rounded-xl text-sm" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#E8ECFF" }}>
          <option value="">All Employees</option>
          {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
      )}

      <div className="space-y-3">
        {reports.map((r) => (
          <div
            key={r.user.user_id}
            className="rounded-2xl p-4 cursor-pointer transition-transform active:scale-[0.99]"
            style={{ background: "#161B2C", border: "1px solid #1E2640" }}
            data-testid={`report-${r.user.user_id}`}
            onClick={() => setDetailUser(r.user)}
            role="button"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold font-syne" style={{ background: "#1E2640", color: "#E8ECFF" }}>
                {r.user.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate" style={{ color: "#E8ECFF" }}>{r.user.name}</div>
                <div className="text-xs" style={{ color: "#7A85A3" }}>{r.user.department || "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-syne font-bold text-3xl" style={{ color: r.pct >= 90 ? "#22D3A5" : r.pct >= 75 ? "#F7C94F" : "#F75A5A" }}>{r.pct}%</div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Attendance</div>
              </div>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ background: "#1E2640" }}>
              <div className="h-full" style={{ width: `${r.pct}%`, background: r.pct >= 90 ? "#22D3A5" : r.pct >= 75 ? "#F7C94F" : "#F75A5A" }} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["P", r.present, "#22D3A5"],["A", r.absent, "#F75A5A"],["H", r.half, "#F7C94F"],["L", r.leave, "#4F8EF7"],["HO", r.holiday, "#9B6FEB"],["D", r.workDays, "#E8ECFF"]].map(([l, v, c]) => (
                <div key={l} className="rounded-lg p-2" style={{ background: "#0A0D14", border: "1px solid #1E2640" }}>
                  <div className="font-syne font-bold text-base" style={{ color: c }}>{v}</div>
                  <div className="text-[9px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>{l === "P" ? "Present" : l === "A" ? "Absent" : l === "H" ? "Half" : l === "L" ? "Leave" : l === "HO" ? "Holiday" : "Days"}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {detailUser && (
        <ReportDetail
          user={detailUser}
          month={month}
          rows={buildRowsFor(detailUser)}
          canEdit={canEditAttendance}
          onEdit={editAttendance}
          onCreate={createAttendance}
          onDelete={deleteAttendance}
          onClose={() => setDetailUser(null)}
        />
      )}
    </div>
  );
}

function ReportDetail({ user, month, rows, canEdit = false, onEdit, onCreate, onDelete, onClose }) {
  const [editingRow, setEditingRow] = useState(null); // { att_id, ci, co }
  const [savingEdit, setSavingEdit] = useState(false);
  const totalHours = rows.reduce((sum, r) => sum + (parseFloat(r.hours) || 0), 0);
  const statusColor = (s) => {
    if (s === "Present") return "#22D3A5";
    if (s === "Half Day") return "#F7C94F";
    if (s.startsWith("Leave")) return "#4F8EF7";
    if (s.startsWith("Holiday")) return "#9B6FEB";
    if (s.startsWith("Sunday")) return "#7A85A3";
    return "#F75A5A";
  };
  const mapsLink = (lat, lng) => lat != null && lng != null
    ? `https://www.google.com/maps?q=${lat},${lng}`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "rgba(10,13,20,0.95)", backdropFilter: "blur(4px)" }}
      data-testid="report-detail-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0" style={{ background: "#0A0D14", borderBottom: "1px solid #1E2640" }}>
        <button onClick={onClose} data-testid="report-detail-close" className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#161B2C", color: "#E8ECFF" }}>
          <X size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-bold truncate" style={{ color: "#E8ECFF" }}>{user.name}</div>
          <div className="text-[11px]" style={{ color: "#7A85A3" }}>
            {user.department || "—"} · {month} · {rows.length} days · {totalHours.toFixed(1)}h total
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto" style={{ background: "#0A0D14" }}>
        <table className="w-full text-xs" data-testid="report-detail-table">
          <thead className="sticky top-0 z-10" style={{ background: "#0A0D14" }}>
            <tr style={{ color: "#7A85A3" }}>
              {[...["Date","Day","In","Out","Hrs","Status","Late","Early","Reg","Address","Map"], ...(canEdit ? ["Edit"] : [])].map((h) => (
                <th key={h} className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-semibold" style={{ borderBottom: "1px solid #1E2640" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const ci = mapsLink(r.ci_lat, r.ci_lng);
              const co = mapsLink(r.co_lat, r.co_lng);
              return (
                <tr key={r.date} data-testid={`detail-row-${r.date}`} style={{ borderBottom: "1px solid #161B2C" }}>
                  <td className="px-2 py-2 font-mono" style={{ color: "#E8ECFF" }}>{r.date.slice(5)}</td>
                  <td className="px-2 py-2" style={{ color: "#7A85A3" }}>{r.day}</td>
                  <td className="px-2 py-2 font-mono" style={{ color: "#E8ECFF" }}>{r.check_in}</td>
                  <td className="px-2 py-2 font-mono" style={{ color: "#E8ECFF" }}>{r.check_out}</td>
                  <td className="px-2 py-2 font-mono" style={{ color: "#7A85A3" }}>{r.hours || "—"}</td>
                  <td className="px-2 py-2 font-semibold" style={{ color: statusColor(r.status) }}>{r.status}</td>
                  <td className="px-2 py-2 font-mono" style={{ color: r.late ? "#fb923c" : "#374151" }}>{r.late ? `${r.late}m` : "—"}</td>
                  <td className="px-2 py-2 font-mono" style={{ color: r.early ? "#fbbf24" : "#374151" }}>{r.early ? `${r.early}m` : "—"}</td>
                  <td className="px-2 py-2" style={{ color: r.regularized ? "#22D3A5" : "#374151" }}>{r.regularized ? "Yes" : "—"}</td>
                  <td className="px-2 py-2 max-w-[260px]" style={{ color: "#E8ECFF" }}>
                    {(r.ci_address || r.co_address) ? (
                      <div className="space-y-0.5">
                        {r.ci_address && (
                          <div className="text-[11px] truncate" title={`Check-In: ${r.ci_address}`}>
                            <span style={{ color: "#22D3A5" }}>In:</span> {r.ci_address}
                          </div>
                        )}
                        {r.co_address && (
                          <div className="text-[11px] truncate" title={`Check-Out: ${r.co_address}`}>
                            <span style={{ color: "#F7C94F" }}>Out:</span> {r.co_address}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: "#374151" }}>—</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      {ci && (
                        <a href={ci} target="_blank" rel="noreferrer" title={`Check-In: ${r.ci_lat?.toFixed(4)}, ${r.ci_lng?.toFixed(4)}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ background: "#0f2a1f", color: "#22D3A5" }}>
                          <MapPin size={10} />
                        </a>
                      )}
                      {co && (
                        <a href={co} target="_blank" rel="noreferrer" title={`Check-Out: ${r.co_lat?.toFixed(4)}, ${r.co_lng?.toFixed(4)}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ background: "#2a1f0f", color: "#F7C94F" }}>
                          <MapPin size={10} />
                        </a>
                      )}
                      {!ci && !co && <span style={{ color: "#374151" }}>—</span>}
                    </div>
                  </td>
                  {canEdit && (
                    <td className="px-2 py-2">
                      {r.att_id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingRow({ att_id: r.att_id, date: r.date, ci: r.check_in === "—" ? "" : r.check_in, co: r.check_out === "—" ? "" : r.check_out, isNew: false }); }}
                            data-testid={`edit-att-${r.date}`}
                            title="Edit times"
                            className="inline-flex items-center justify-center w-6 h-6 rounded"
                            style={{ background: "#0f1a2a", color: "#4F8EF7" }}
                          ><Pencil size={11} /></button>
                          <button
                            onClick={async (e) => { e.stopPropagation(); try { await onDelete(r.att_id); } catch (err) { window.alert(err?.response?.data?.detail || "Failed"); } }}
                            data-testid={`delete-att-${r.date}`}
                            title="Delete row"
                            className="inline-flex items-center justify-center w-6 h-6 rounded"
                            style={{ background: "#2a0f0f", color: "#F75A5A" }}
                          ><Trash2 size={11} /></button>
                        </div>
                      ) : (r.status === "Absent" || r.status.startsWith("Sunday")) ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingRow({ att_id: null, date: r.date, ci: "", co: "", isNew: true }); }}
                          data-testid={`mark-present-${r.date}`}
                          title="Mark present (add punch)"
                          className="inline-flex items-center justify-center px-2 h-6 rounded text-[10px] font-bold gap-1"
                          style={{ background: "#0f2a1f", color: "#22D3A5" }}
                        ><Pencil size={10} /> Add</button>
                      ) : <span style={{ color: "#374151" }}>—</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="text-center py-12" style={{ color: "#7A85A3" }}>No data for {month}.</div>
        )}
      </div>

      {editingRow && (
        <div className="fixed inset-0 z-60 flex items-end justify-center p-4" style={{ background: "rgba(10,13,20,0.7)" }} onClick={() => setEditingRow(null)} data-testid="edit-att-modal">
          <div className="w-full max-w-sm rounded-2xl p-5 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-bold" style={{ color: "#E8ECFF" }}>{editingRow.isNew ? "Mark Present · " : "Edit Attendance · "}{editingRow.date}</div>
              <button onClick={() => setEditingRow(null)} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#0A0D14", color: "#7A85A3" }}><X size={16} /></button>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Check-In (HH:MM, 24h, IST)</label>
              <input data-testid="edit-att-checkin" type="time" value={editingRow.ci || ""} onChange={(e) => setEditingRow({ ...editingRow, ci: e.target.value })} className="w-full h-11 px-3 rounded-xl font-mono" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Check-Out (HH:MM, 24h, IST)</label>
              <input data-testid="edit-att-checkout" type="time" value={editingRow.co || ""} onChange={(e) => setEditingRow({ ...editingRow, co: e.target.value })} className="w-full h-11 px-3 rounded-xl font-mono" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            </div>
            <div className="text-[10px]" style={{ color: "#7A85A3" }}>Times are read in the company timezone (Asia/Kolkata). This action is logged and the admin will be notified in the daily summary email.</div>
            <button
              onClick={async () => {
                if (editingRow.isNew && !editingRow.ci) { window.alert("Check-In is required to mark Present"); return; }
                setSavingEdit(true);
                try {
                  const body = {};
                  if (editingRow.ci) body.check_in = editingRow.ci;
                  if (editingRow.co) body.check_out = editingRow.co;
                  if (editingRow.isNew) {
                    await onCreate(user.user_id, editingRow.date, body);
                  } else {
                    await onEdit(editingRow.att_id, body);
                  }
                  setEditingRow(null);
                } catch (err) {
                  window.alert(err?.response?.data?.detail || "Save failed");
                } finally { setSavingEdit(false); }
              }}
              disabled={savingEdit}
              data-testid="edit-att-save"
              className="w-full h-11 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "#22D3A5", color: "#0A0D14" }}
            ><Save size={16} /> {savingEdit ? "Saving…" : editingRow.isNew ? "Mark Present" : "Save Corrections"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
